import { h } from 'preact';
import React from 'react';

import injectQueryParams from '../../injectQueryParams';
import Children from './Children';
import { SafeArea } from '../../Components/SafeArea/SafeArea';

import { PrivacyManagerSettings } from './PrivacyManagerSettings';
import privacyManagerStarter from './privacyManagerStarter';
import {
  addStylesToHead,
  convertClassName,
  setFirstAndLastElementsOnTheModal,
  getLanguage,
  getIframeTitle,
  includesComponent,
  isValidDevOrigin,
  deepClone,
} from '../../util';
import { DefaultLanguageContext, IsPMContext } from '../../contexts';
import { actionTypes, lockedVendors } from '../../constants';
import SpatialNavigation from 'spatial-navigation-js';
import './privacyManager.css';

const blank = { categories: [], specialPurposes: [], features: [], specialFeatures: [], vendors: [] };
export class PrivacyManager extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      choices: [],
      components: props.messageId ? [{}, {}] : privacyManagerStarter,
      pmData: {},
      pmLoaded: null,
      siteId: null,
      current: [0],
      loading: true,
      shown: false,
      actions: [],
      width: 0,
      height: 0,
      devOpts: { hideRowColumnBorder: false, },
      highlight: null,
      allSelectionsMade: false,
      neutralPm: false,
      currentView: props.currentView ? props.currentView : 'HOME',
      hideTemplatesButton: false,
    };
  }

  componentDidMount = () => {
    const { messageId } = this.props;
    window.addEventListener('keydown', this.handleFirstTab);
    SpatialNavigation.init();
    // IF MESSAGE ID
    if (messageId) {
      fetch(`${this.props.mmsOrigin}/message?message_id=${messageId}`, { insecure: true })
        .then(r => r.json())
        .then(data => {
          try {
            const parsedComponents = JSON.parse(data.message_json);
            const components = Array.isArray(parsedComponents) ? parsedComponents : parsedComponents.siblings
              ? parsedComponents.siblings : [parsedComponents];
            const siteId = this.props.siteId || data.site_id;
            if (components[0].css) addStylesToHead(components[0].css);
            this.setState({
              components,
              type: components[0].settings.type,
              siteId: siteId || null,
              loading: false,
              choices: data.message_choice || [],

            }, () => {
              window.addEventListener('message', this.handleMessages, false);
              this.setupPrivacyManager(siteId, components[0].settings.vendorList);
              this.setState({ actions: [] });
            });
          } catch (err) {
            throw Error(`Could not parse PM data:${err}`);
          };
        })
        .catch(() => {
          throw { code: 1, message: `Could not load PM with id ${messageId}` };
        });

      if (this.props.preloadConsent === "true") {
        this.preloadConsent()
      };
    } else if (this.props.dev || this.props.tiny || this.props.mini || this.props.full) {
      this.setState({ loading: false });
      window.addEventListener('message', this.handleDev, false);
    }
  }

  updateAllSelectionsBool = (pmData) => {
    if (this.state.neutralPm && this.state.pmData.vendors && this.state.pmData.categories) {
      const allSelectionsMade =
        pmData.vendors.every(v => v.consentCategories.length === 0 || v.active !== null)
        && pmData.categories.every(c => {
          return (c.requiringConsentVendors.length === 0 || c.active !== null || c.disclosureOnly)
            && ((c.requireConsent && c.requiringConsentVendors.length ? c.active : true))
        })
        && (!pmData.specialFeatures.length || pmData.specialFeatures.every(f => f.active !== null))

      this.setState({ allSelectionsMade })
    }
  }

  handleFirstTab = (e) => {
    if (e.keyCode === 9) {
      document.body.classList.add('user-is-tabbing');
      window.removeEventListener('keydown', this.handleFirstTab);
    }
  }

  preloadConsent = () => {
    this.consentPromise = new Promise((resolve) => {
      this.preloadFunction = ({ data }) => {
        if (data.name === 'sp.loadConsent') {
          data.consent = data.consent || {}

          resolve({
            categories: [],
            legIntCategories: [],
            vendors: [],
            legIntVendors: [],
            specialFeatures: [],
            hasConsentData: data.consent.consentStatus && data.consent.consentStatus.hasConsentData,
            ...data.consent,
          })
        }
      }

      window.addEventListener('message', this.preloadFunction, false);
      window.parent.postMessage({ name: 'sp.readyForPreloadConsent', messageId: this.props.messageId, }, '*');
    }).then((res) => {
      window.removeEventListener('message', this.preloadFunction)
      return res;
    })
  }

  setupPrivacyManager = (siteId, vendorListId, dev) => {
    const component = this.state.components ? this.state.components[0] : {};
    const Settings = new PrivacyManagerSettings(component.settings || {});

    const language = getLanguage(Settings, this.props.consentLanguage, component);

    if (siteId && !dev || siteId && vendorListId) {
      let pmData = {};
      fetch(`${this.props.cmpv2Origin}/privacy-manager/privacy-manager-view?siteId=${siteId}&vendorListId=${vendorListId}&consentLanguage=${language}${dev ? '&preview=true' : ''}`, {
        insecure: true
      }).then(r => r.json())
        .then(pData => {
          pmData = pData || blank;
          if (dev) {
            return false;
          } else if (this.props.preloadConsent === "true") {
            return this.consentPromise;
          } else {
            return fetch(`${this.props.cmpv2Origin}/consent/v3/${siteId}?consentUUID=${this.props.consentUUID}&consentLanguage=${language}&separateLegIntVendors=true`, { insecure: true })
              .then(r => (r ? r.json() : null));
          }
        })
        .then(
          cData => {
            if (cData && cData.hasConsentData !== false) {
              this.setupConsent(pmData, cData);
            } else {
              this.setupConsent(pmData);
            }
          },
          // we want to catch a network error but not a setupConsent error
          (err) => {
            this.setupConsent(pmData || blank);
          }
        )
    }
  }

  /* SETUP THE CONSENT */
  setupConsent = (pmData, consentData) => {
    const hasConsent = consentData ? true : false;
    const neutralPm = includesComponent('PMTCFv2InlineNeutral', this.state.components[0])
    const consent = consentData || { stacks: [], categories: [], vendors: [], specialFeatures: [], legIntCategories: [], legIntVendors: [], hasConsentData: false };
    pmData.stacks = pmData.stacks || [];

    pmData.categories = (pmData.categories || []).map(i => {
      return {
        ...i,
        legInt: consent.legIntCategories.indexOf(i._id) > -1 ? true : ((i.legIntVendors.length > 0 || consent.categories.indexOf(i._id) > -1) && !hasConsent ? true : null),
        active: !neutralPm
          ? (consent.categories.indexOf(i._id) > -1 ? true : false)
          : (hasConsent ? (consent.categories.indexOf(i._id) > -1 ? true : false) : null),
        required: neutralPm
      };
    });

    pmData.specialFeatures = (pmData.specialFeatures || [])
      .map(i => ({
        ...i,
        active: !neutralPm
          ? consent.specialFeatures.indexOf(i._id) > -1 ? true : false
          : (hasConsent ? (consent.specialFeatures.indexOf(i._id) > -1 ? true : false) : null),
      }));

    pmData.vendors = (pmData.vendors || []).map(i => {
      const isLocked = lockedVendors.includes(i.iabId);
      const isActive = !neutralPm
        ? consent.vendors.includes(i.vendorId)
        : (hasConsent ? (consent.vendors.includes(i.vendorId) ? true : false) : null)
      const legInt = consent.legIntVendors.includes(i.vendorId)
        || (i.legIntCategories.length > 0 && !hasConsent)
        || null;

      const cookies = i.cookies.map(cookie => ({
        ...cookie,
        categoryName: (pmData.categories.find(({ _id }) => cookie.categoryId === _id) || {}).name,
      }));

      return {
        ...i,
        legInt,
        active: isLocked ? false : isActive,
        cookies,
      };
    });

    this.setState({ pmData, neutralPm }, () => {
      this.updateAllSelectionsBool(pmData)
      this.showMessage()
    });
  }

  showMessage = () => {
    const component = this.state.components[0];
    if (component.settings.type !== 'inline') setFirstAndLastElementsOnTheModal();
    if (!this.state.shown) {
      this.setState({ shown: true });
      window.parent.postMessage({
        name: 'sp.showMessage',
        actions: [],
        settings: component.settings,
        iframeTitle: getIframeTitle(component.settings, this.props.consentLanguage, component),
        messageId: this.props.messageId,
        fromPM: true,
      }, '*');
    }
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.handleDev);
    window.removeEventListener('message', this.handleMessages);
  }

  getLockedVendors = (pmData, exclude, legInt) => {
    let lockedVendors = [];
    pmData.categories.forEach(c => {
      const shouldExclude = exclude && exclude.length > 0 ? exclude.indexOf(c._id) > -1 : false;
      if (legInt) {
        if (c.legInt && !shouldExclude) lockedVendors = [...lockedVendors, ...c.legIntVendors.map(v => v.vendorId)];
      } else {
        if (c.active && !shouldExclude) lockedVendors = [...lockedVendors, ...c.requiringConsentVendors.map(v => v.vendorId)];
      };
    })
    pmData.specialFeatures.forEach(sf => {
      if (sf.active) lockedVendors = [...lockedVendors, ...sf.vendors.map(v => v.vendorId)]
    })

    return lockedVendors;
  }

  togglePurposeOrFeature = (id, type, value, vendors, legInt, exclude) => {
    const pmData = Object.assign({}, this.state.pmData);
    const key = type === 'purposes' ? 'categories' : 'specialFeatures';

    /* UPDATE TOGGLE FOR SPECIAL FEATURE OR PURPOSE */
    pmData[key].map(i => {
      if (id && i._id === id && legInt) i.legInt = value;
      if (id && i._id === id && !legInt) i.active = value;
    });

    /* UPDATE TOGGLE FOR VENDORS */
    if (value === true) {
      pmData.vendors.map(v => {
        const exists = vendors.find(pfVendor => pfVendor.vendorId === v.vendorId);
        if (exists) v[legInt ? 'legInt' : 'active'] = true;
        return v;
      });
    } else {
      if (key === 'specialFeatures') {
        const lockedVendorsLegitInterest = this.getLockedVendors(pmData, exclude, true);
        const lockedVendorsConsent = this.getLockedVendors(pmData, exclude, false);

        pmData.vendors.map(v => {
          const lockedConsent = lockedVendorsConsent.find(lv => lv === v.vendorId);
          const lockedLegitInterest = lockedVendorsLegitInterest.find(lv => lv === v.vendorId);
          const exists = vendors.find(pfVendor => pfVendor.vendorId === v.vendorId);
          if (exists && !lockedConsent) v.active = false;
          if (exists && !lockedLegitInterest) v.legInt = false;
          return v;
        });

      } else {
        const lockedVendors = this.getLockedVendors(pmData, exclude, legInt);
        pmData.vendors.map(v => {
          const exists = vendors.find(pfVendor => pfVendor.vendorId === v.vendorId);
          const locked = lockedVendors.find(lv => lv === v.vendorId);
          if (exists && !locked) v[legInt ? 'legInt' : 'active'] = false;
          return v;
        });
      }
    }

    this.updateAllSelectionsBool(pmData)

    this.setState({ pmData });
  }

  toggleVendor = (id, value, type = 'active') => {
    const pmData = Object.assign({}, this.state.pmData);
    pmData.vendors.map(v => {
      if (v.vendorId === id) {
        if (type === 'all' && v.legIntCategories.length && v.consentCategories.length) {
          if (v.active || v.legInt) {
            v.active = false;
            v.legInt = false;
          } else {
            v.active = true;
            v.legInt = true;
          }
        } else if (type === 'all') {
          v[v.legIntCategories.length ? 'legInt' : 'active'] = value;
        } else if (type === 'legInt' && value === false && !v.consentCategories.length && v.iabSpecialPurposes.length) {
          //edgcase where a vendor falls under special purpose and legit interest, and not consent (for inline ui)
          v.active = false;
          v.legInt = false;
        } else {
          v[type] = value;
        }
      }
      return v;
    });
    this.updateAllSelectionsBool(pmData)

    this.setState({ pmData });
  }

  toggleStack = (e, purposes, value, legInt) => {
    e !== undefined && e.preventDefault();
    // EXCLUDE ALL PURPOSES FOR THAT STACK FROM BEING LOCKED
    const exclude = purposes.map(p => p._id);
    purposes.forEach(p => {
      const vendors = legInt ? p.legIntVendors : p.requiringConsentVendors;
      this.togglePurposeOrFeature(p._id, 'purposes', value, vendors || [], legInt, exclude);
    })
  }

  makeChoice = (choiceId, consentLanguage, _, choiceData = {}) => {
    if (choiceId === 'CUSTOM_VIEW') {
      return this.setState({ currentView: 'CUSTOM_VIEW' })
    }

    if (choiceId === 'HOME') {
      return this.setState({ currentView: 'HOME' })
    }

    const choice = this.state.choices.find(c => c.button_text === choiceData.button_text) || {};

    const { pmData } = this.state;
    SpatialNavigation.uninit();

    window.parent.postMessage({
      name: "sp.hideMessage",
      fromPM: true,
      actionType: choiceId,
      choice_id: choice.choice_id,
      consentLanguage,
      messageId: this.props.messageId,
      customAction: choice.type === 9 ? choiceData.js_fn_name : null,
      payload: {
        privacyManagerId: this.props.messageId,
        lan: consentLanguage,
        categories: pmData.categories.filter(cf => (cf.active || cf.legInt)).map(c => ({
          _id: c._id,
          type: c.type,
          iabId: c.iabId,
          consent: c.active ? true : false,
          legInt: c.legInt ? true : false
        })),
        vendors: pmData.vendors.filter(vf => vf.active || vf.legInt).map(v => ({
          _id: v.vendorId,
          vendorType: v.vendorType,
          iabId: v.iabId,
          consent: Boolean(v.active),
          legInt: Boolean(v.legInt),
        })),
        specialFeatures: pmData.specialFeatures.filter(sf => sf.active).map(f => ({ _id: f._id, iabId: f.iabId }))
      },
      onPrivacyManagerAction: this.onPrivacyManagerActionData(choiceId)
    }, '*');
  }

  onPrivacyManagerActionData = (choiceId) => {
    const { pmData } = this.state;
    let purposeConsent, vendorConsent;
    // ACCEPT_ALL and REJECT_ALL don't update our pmData
    if (actionTypes.ACCEPT_ALL === choiceId) {
      purposeConsent = vendorConsent = 'all';
    } else if (actionTypes.REJECT_ALL === choiceId) {
      purposeConsent = vendorConsent = 'none';
    } else {
      // SAVE_AND_EXIT or CANCEL means we should look at our pmData 
      let purposeConsentCount = 0;
      let vendorConsentCount = 0;
      let filteredVendors = pmData.vendors.filter(v => v.consentCategories.length || v.legIntCategories.length)
      filteredVendors.forEach((vf) => { if (vf.active) vendorConsentCount++ });
      pmData.categories.forEach((cf) => { if (cf.active) purposeConsentCount++ });

      purposeConsent = purposeConsentCount === pmData.categories.length ? 'all' : (purposeConsentCount === 0 ? 'none' : 'some');
      vendorConsent = vendorConsentCount === filteredVendors.length ? 'all' : (vendorConsentCount === 0 ? 'none' : 'some');
    }
    return {
      purposeConsent,
      vendorConsent,
      vendorList: undefined
    };
  }

  handleMessages = ({ data }) => {
    if (data.name === 'sp.choiceAction' && data.type) {
      const choice = this.state.choices.find(c => c.type === data.type);
      const noticeComponent = this.state.components ? this.state.components[0] : {};
      if (choice) {
        window.parent.postMessage({
          name: 'sp.hideMessage',
          actions: [{ type: "choice", data: choice }],
          settings: noticeComponent.settings || {},
          messageId: this.props.messageId,
        }, '*');
      }
    }
  }

  handleDev = (event) => {
    if (!isValidDevOrigin(event)) {
      return
    }
    if (event.data && typeof event.data === 'string') {
      const messageData = JSON.parse(event.data);
      if (messageData.components[0].css) addStylesToHead(messageData.components[0].css);
      this.setState({
        devOpts: messageData.devOpts || {},
        components: messageData.components,
        current: messageData.current,
        siteId: messageData.siteId,
        highlight: messageData.highlight,
        neutralPm: includesComponent('PMTCFv2InlineNeutral', messageData.components[0]),
        hideTemplatesButton: messageData.hideTemplatesButton,
      });

      const vendorListId = messageData.components[0].settings.vendorList;
      const languageChanged = (messageData.components[0].settings.defaultLanguage !== this.state.components[0].settings.defaultLanguage);
      const browserDefaultChanged = (messageData.components[0].settings.useBrowserDefault !== this.state.components[0].settings.useBrowserDefault);
      const neutralPmChanged = this.state.neutralPm !== includesComponent('PMTCFv2InlineNeutral', messageData.components[0])

      if (!this.state.pmLoaded || this.state.pmLoaded !== vendorListId || languageChanged || browserDefaultChanged || neutralPmChanged) {
        this.setState({ pmLoaded: vendorListId }, () => {
          this.setupPrivacyManager(messageData.siteId, messageData.components[0].settings.vendorList, true)
        });
      }
    }
  }

  noticeSetCurrent = (newCurrent, e) => {
    let target = e.target === e.currentTarget;
    if (e.currentTarget.className.includes('text')) target = true;
    if (e.currentTarget.className.includes('message-stacks')) target = true;
    if (target) {
      window.parent.postMessage({ action: 'dev.setCurrent', newCurrent }, '*');
    }
  };

  doAction = (action) => window.parent.postMessage({ action }, '*')

  render() {
    const noticeComponent = this.state.components ? this.state.components[this.state.currentView === 'CUSTOM_VIEW' ? 1 : 0] : {};
    const components = noticeComponent.children || [];
    const Settings = new PrivacyManagerSettings(noticeComponent.settings || {});
    let noticeClass = this.props.dev ? 'message message-dev' : 'message';

    const styles = {
      ...Settings.padding,
      ...Settings.border
    }, closeStyles = { ...Settings.closeFont }, veilStyles = {};

    styles.background = Settings.background;

    /* WIDTH & HEIGHT */
    const { paddingTop, paddingBottom, paddingLeft, paddingRight } = Settings.padding;
    styles.width = Settings.width.type === 'auto' ? 'auto' : Settings.width.value + Settings.width.type;
    styles.height = Settings.height.type === 'auto' ? 'auto' : Settings.height.value + Settings.height.type;
    if (Settings.height.type === '%') {
      styles.height = `calc(${Settings.height.value}% - ${(paddingTop + paddingBottom)}px`;
    }
    if (Settings.width.type === '%' && (Settings.type === 'top' || Settings.type === 'bottom')) {
      styles.width = `calc(${Settings.width.value}% - ${(paddingLeft + paddingRight)}px`;
    }

    /* Close Button */
    if (Settings.showClose) {
      if (Settings.closeAlign === 'left') {
        closeStyles.right = 'auto';
        closeStyles.left = '20px';
        closeStyles.textAlign = 'left';
      } else {
        closeStyles.left = 'auto';
        closeStyles.right = '20px';
        closeStyles.textAlign = 'right';
      }
      closeStyles.zIndex = 2147483646;
    } else {
      closeStyles.display = 'none';
    }

    /* VEIL STYLES */
    veilStyles.display = Settings.showVeil ? 'flex' : 'none';
    veilStyles.background = Settings.veilColor;
    veilStyles.opacity = Settings.veilOpacity;


    const preview = this.props.tiny || this.props.mini;
    const fixed = Settings.type === 'top' || Settings.type === 'bottom';
    const scale = this.props.mini ? 0.4 : (this.props.full ? 1.0 : 0.2);
    if (preview) {
      styles.transform = `scale(${scale}) translateX(-50%) translateY(-50%)`;
      if (fixed) styles.transform = `scale(${scale}) translateX(-50%)`;
      styles.transformOrigin = Settings.type === 'bottom' ? 'left bottom' : 'left top';
      noticeClass = noticeClass + ' message-mini';
    }
    if (preview && (fixed || Settings.width.type !== 'px') && !this.props.full) {
      const previewWidth = this.props.mini ? 364 : 160;
      const percent = fixed || Settings.width.type === 'auto' ? 100 : Settings.width.value;
      const scaledWidth = ((1 / scale) * previewWidth * (100 / percent)) - (paddingLeft + paddingRight);
      styles.width = scaledWidth + 'px';
    }

    if (Settings.type === 'modal') noticeClass += ' type-modal';
    if (Settings.type === 'top') noticeClass += ' type-top';
    if (Settings.type === 'bottom') noticeClass += ' type-bottom';

    const dev = {
      current: JSON.stringify(this.state.current || [0]),
      opts: this.state.devOpts,
      add: () => this.doAction('dev.add'),
      templates: () => this.doAction('dev.templates'),
      highlight: this.state.highlight,
      hover: {
        onClick: (e) => {
          e.preventDefault();
          const target = e.target && e.target.getAttribute('path') ? JSON.parse(e.target.getAttribute('path')) : null;
          if (target) {
            this.noticeSetCurrent(target, e);
          } else if (!target && e.currentTarget && (e.currentTarget.className.includes('text') || e.currentTarget.className.includes('message-stacks'))) {
            const newTarget = e.currentTarget && e.currentTarget.getAttribute('path') ? JSON.parse(e.currentTarget.getAttribute('path')) : null;
            if (newTarget) this.noticeSetCurrent(newTarget, e);
          }
        },
        onMouseOver: (e) => {
          e.preventDefault();
          let target = e.target;
          if (e.currentTarget.className.includes('text')) target = e.currentTarget;
          if (e.currentTarget.className.includes('message-stacks')) target = e.currentTarget;
          if (target.className.includes('message-component')) target.id = 'on-hover';
        },
        onMouseOut: (e) => {
          e.preventDefault();
          let target = e.target;
          if (e.currentTarget.className.includes('text')) target = e.currentTarget;
          if (e.currentTarget.className.includes('message-stacks')) target = e.currentTarget;
          target.id = 'no-hover';
        }
      }
    };
    const className = convertClassName(noticeComponent.handle);
    const notice = (
      <div ref={element => this.noticeRef = element} className={noticeClass} style={styles}>
        <div className="message-stacksclose" onClick={this.dismissNotice} style={closeStyles}>X</div>
        {Children({
          path: [0],
          dev: this.props.dev ? dev : null,
          components: components,
          siteId: this.state.siteId,
          vendorListId: Settings.vendorList || null,
          message: Settings,
          makeChoice: this.makeChoice,
          language: getLanguage(Settings, this.props.consentLanguage, noticeComponent),
          togglePurposeOrFeature: this.togglePurposeOrFeature,
          toggleVendor: this.toggleVendor,
          toggleStack: this.toggleStack,
          width: this.state.width,
          pmData: this.state.pmData,
          pmTab: this.props.pmTab,
          preview: preview,
          idfaStatus: this.props.idfaStatus,
          neutralPm: this.state.neutralPm,
          allSelectionsMade: this.state.allSelectionsMade,
          shown: this.state.shown
        })}
      </div>
    );

    const emptyNotice = (
      <div id="empty-notice" role="presentation">
        <button onClick={this.props.dev ? dev.add : null} className="empty-add">+ Add Components</button>
        {!this.state.hideTemplatesButton && (
          <>
            <div className="empty-or">or</div>
            <button className="empty-template" onClick={this.props.dev ? dev.templates : null}>Start from Template</button>
          </>
        )}
      </div>
    );

    let noticeContainer = (
      <div
        role="dialog"
        aria-label="privacy manager"
        className={'message-container' + className}
      >
        {Settings.useSafeArea ? '' : <div className="message-overlay" style={veilStyles} />}
        {components.length ? notice : emptyNotice}
      </div>
    );

    noticeContainer = Settings.useSafeArea
      ? <SafeArea veil={<div className="message-overlay" style={veilStyles} />}>{noticeContainer}</SafeArea>
      : noticeContainer;

    if (this.state.loading) return (
      <div className="loader-container">
        <div className="loader" />
      </div>
    );

    return (
      <IsPMContext.Provider value={true}>
        <DefaultLanguageContext.Provider value={Settings.language || Settings.defaultLanguage}>
          {
            (this.props.mini || this.props.tiny || this.props.full)
              ? notice
              : noticeContainer
          }
        </DefaultLanguageContext.Provider>
      </IsPMContext.Provider>
    );
  }
}

export default injectQueryParams(PrivacyManager);